import { IGoogleAnalyticsData, IInsuranceApp, IInsuranceAppSummary, IExternalAppSummary } from 'types/customTypes';
import { IAnalyticsDataField } from 'services/Analytics';
import { getAppFinishEventStepName, setGlobalGAVariables } from './utils';
import { analyticsInputChangeUtils } from './inputChangeUtils';
import {
    AnalyticsEvent,
    IAnalyticsAdTrax,
    IAnalyticsApplication,
    IAnalyticsAppInputChange,
    IAnalyticsAppAddressScrubbing,
    IAnalyticsFieldError,
    IAnalyticsBanner,
    IAnalyticsUserData,
    IAnalyticsRiderDisplay,
    IAnalyticsProgressItemListClick,
    IAnalyticsPixelFire,
    IAnalyticsProductNotAvailable
} from './types';

export const fireApplicationEvent = (
    evtName: AnalyticsEvent,
    segmentTitle: string = '',
    googleAnalyticsData?: IGoogleAnalyticsData
): void => {
    const detail: IAnalyticsApplication = {
        productCode: googleAnalyticsData?.productCode || '',
        channel: googleAnalyticsData?.channel || 'Web',
        premiumAmount: googleAnalyticsData?.premiumAmount || '',
        coverageAmount: googleAnalyticsData?.coverageAmount || '',
        segmentTitle
    };
    // set global variable for GTM to use
    setGlobalGAVariables(googleAnalyticsData || {}, segmentTitle);
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireApplicationFinishEvent = (
    evtName: AnalyticsEvent,
    insuranceAppSummary?: IInsuranceAppSummary
): void => {
    let detail: IAnalyticsApplication;
    if (!insuranceAppSummary) {
        const stepName = getAppFinishEventStepName();
        detail = {
            segmentTitle: stepName
        } as IAnalyticsApplication;
    } else {
        const { googleAnalyticsData, resultCode, recordState } = insuranceAppSummary;
        const stepName = getAppFinishEventStepName(resultCode, recordState);
        detail = {
            productCode: googleAnalyticsData.productCode || '',
            channel: googleAnalyticsData.channel || 'Web',
            policyStatus: googleAnalyticsData.policyStatus || '',
            premiumAmount: googleAnalyticsData.premiumAmount || '',
            coverageAmount: googleAnalyticsData.coverageAmount || '',
            segmentTitle: stepName
        };
        // set global variable for GTM to use
        setGlobalGAVariables(detail, stepName);
    }
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireAdTraxEvent = (evtName: AnalyticsEvent, adTraxNumber: string = ''): void => {
    const detail: IAnalyticsAdTrax = {
        adTraxValue: adTraxNumber
    };
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireUserDataEvent = (evtName: AnalyticsEvent, key: string, value: string): void => {
    const detail: IAnalyticsUserData = {
        [key]: value
    };
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireApplicationInputChangeEvent = (
    evtName: AnalyticsEvent,
    fieldName: string,
    fieldAction: string,
    insuranceApp?: IInsuranceApp,
    delay?: boolean,
    allowDuplicateEvents: boolean = true
): void => {
    if (!insuranceApp || !fieldName || !fieldAction) return;
    const { segment, googleAnalyticsData } = insuranceApp;
    const { segmentTitle } = segment;
    const detail: IAnalyticsAppInputChange = {
        productCode: googleAnalyticsData.productCode || '',
        channel: googleAnalyticsData.channel || 'Web',
        premiumAmount: googleAnalyticsData.premiumAmount || '',
        coverageAmount: googleAnalyticsData.coverageAmount || '',
        segmentTitle: segmentTitle || '',
        fieldName,
        fieldAction
    };
    // skip sending event if it is a duplicate
    if (!allowDuplicateEvents && analyticsInputChangeUtils.eventIsDuplicate(evtName, detail)) {
        return;
    }
    // delay dispatch of the event to ensure dataLayer is fully set
    // this basically ensures clicking on add/remove repeatable fieldGroup buttons
    // does not cause duplicate events to fire due to trackOtherButtons firing prior
    // to handleInputChange/handleBlur events
    if (delay) {
        setTimeout(() => {
            document.dispatchEvent(
                new CustomEvent(evtName, {
                    detail
                })
            );
        }, 2000);

        return;
    }
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireAppStepperButtonClickEvent = (evtName: AnalyticsEvent, detail: IAnalyticsAppInputChange): void => {
    // update global variable for GTM to use
    if (window.truStData) {
        window.truStData.coverageAmount = detail.coverageAmount;
        window.truStData.premiumAmount = detail.premiumAmount;
    }
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireApplicationAddressScrubbingEvent = (evtName: AnalyticsEvent, action: string, label: string): void => {
    if (!action || !label) return;
    const detail: IAnalyticsAppAddressScrubbing = {
        action,
        label
    };
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireFieldErrorEvent = (
    evtName: AnalyticsEvent,
    fieldName: string,
    errorValue: string,
    insuranceApp?: IInsuranceApp
): void => {
    if (!insuranceApp || !fieldName) return;
    const { segment, googleAnalyticsData } = insuranceApp;
    const { segmentTitle } = segment;
    const fieldData = segment.fieldGroups
        .flatMap((group) => group.dataFields)
        .find((field) => field?.fieldCode === fieldName);
    const detail: IAnalyticsFieldError = {
        productCode: googleAnalyticsData.productCode || '',
        channel: googleAnalyticsData.channel || 'Web',
        premiumAmount: googleAnalyticsData.premiumAmount || '',
        coverageAmount: googleAnalyticsData.coverageAmount || '',
        segmentTitle: segmentTitle || '',
        fieldName: (fieldData as IAnalyticsDataField)?.googleAnalytics?.fieldName || fieldName,
        errorValue
    };
    // the global "truStData" variable is already set from fireApplicationEvent running, so we don't need to set it for this event
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireBannerEvent = (
    evtName: AnalyticsEvent,
    bannerMessage: string,
    insuranceApp?: IInsuranceApp,
    fieldAction?: string
): void => {
    if (!insuranceApp || !bannerMessage) return;
    const { segment, googleAnalyticsData } = insuranceApp;
    const { segmentTitle } = segment;
    const detail: IAnalyticsBanner = {
        productCode: googleAnalyticsData.productCode || '',
        channel: googleAnalyticsData.channel || 'Web',
        premiumAmount: googleAnalyticsData.premiumAmount || '',
        coverageAmount: googleAnalyticsData.coverageAmount || '',
        segmentTitle: segmentTitle || '',
        bannerMessage,
        fieldAction
    };
    // the global "truStData" variable is already set from fireApplicationEvent running, so we don't need to set it for this event
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireRiderDisplayEvent = (
    evtName: AnalyticsEvent,
    riderDetails: { productCode: string; coverageAmount: string; premiumAmount: string },
    insuranceApp: IInsuranceApp
): void => {
    if (!insuranceApp || !riderDetails) return;
    const detail: IAnalyticsRiderDisplay = {
        segmentTitle: insuranceApp.segment.segmentTitle || '',
        productCode: riderDetails.productCode,
        coverageAmount: riderDetails.coverageAmount,
        premiumAmount: riderDetails.premiumAmount
    };
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireProgressItemListClickTracker = (
    evtName: AnalyticsEvent,
    insuranceApp?: IInsuranceApp,
    fieldName: string = '',
    fieldAction: string = ''
): void => {
    if (!insuranceApp) return;
    const detail: IAnalyticsProgressItemListClick = {
        fieldName,
        fieldAction,
        stepName: insuranceApp?.segment.segmentTitle || '',
        channel: insuranceApp?.googleAnalyticsData?.channel || 'Web',
        productCode: insuranceApp?.googleAnalyticsData?.productCode || '',
        coverageAmount: insuranceApp?.googleAnalyticsData?.coverageAmount || '',
        premiumAmount: insuranceApp?.googleAnalyticsData?.premiumAmount || ''
    };
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const firePixelsStartSummary = (
    evtName: AnalyticsEvent,
    app: IInsuranceApp | IExternalAppSummary,
    spotName: string = ''
): void => {
    if (!app) return;
    const isInsuranceApp = (obj: IInsuranceApp | IExternalAppSummary): obj is IInsuranceApp =>
        'segment' in obj && 'googleAnalyticsData' in obj;
    const detail: IAnalyticsPixelFire = isInsuranceApp(app)
        ? {
              spotName,
              productCode: app.googleAnalyticsData.productCode || ''
          }
        : {
              spotName,
              productCode: app.productCode || ''
          };
    document.dispatchEvent(
        new CustomEvent(evtName, {
            detail
        })
    );
};

export const fireBasicEvent = (evtName: AnalyticsEvent): void => {
    // global "truStData" variable is already set and not updated as part of
    // this method.
    // so we don't need to set it for this event
    document.dispatchEvent(new CustomEvent(evtName));
};

export const fireProductNotAvailableEvent = (
    event: AnalyticsEvent,
    pnaReason: string = '',
    productCode: string = '',
    channel: string = 'Web',
    premiumAmount: string = '',
    coverageAmount: string = ''
): void => {
    const detail: IAnalyticsProductNotAvailable = {
        segmentTitle: 'Product Not Available',
        channel,
        productCode,
        premiumAmount,
        coverageAmount,
        pnaReason
    };
    document.dispatchEvent(
        new CustomEvent(event, {
            detail
        })
    );
};

export const fireDlvPageEvent = (
    segmentTitle: string,
    policyStatus?: string,
    productCode: string = '',
    channel: string = 'Web',
    premiumAmount: string = '',
    coverageAmount: string = ''
): void => {
    const detail: IAnalyticsApplication = {
        segmentTitle,
        channel,
        productCode,
        premiumAmount,
        coverageAmount,
        ...(policyStatus ? { policyStatus } : {})
    };
    if (window.truStData && policyStatus) {
        window.truStData.policyStatus = policyStatus;
        window.truStData.summaryStatus = policyStatus;
        window.truStData.insuredStatus = policyStatus;
    }
    document.dispatchEvent(new CustomEvent(AnalyticsEvent.APPLICATION_FUNNEL, { detail }));
};

export const fireFieldInteractionEvent = (
    evtName: AnalyticsEvent,
    segmentTitle: string,
    fieldName: string,
    fieldAction: string,
    productCode: string = '',
    channel: string = 'Web',
    premiumAmount: string = '',
    coverageAmount: string = ''
): void => {
    const detail: IAnalyticsAppInputChange = {
        segmentTitle,
        channel,
        productCode,
        premiumAmount,
        coverageAmount,
        fieldName,
        fieldAction
    };
    document.dispatchEvent(new CustomEvent(evtName, { detail }));
};

export const fireFieldErrorEventBasic = (
    evtName: AnalyticsEvent,
    fieldName: string,
    errorValue: string,
    segmentTitle: string,
    productCode: string = '',
    channel: string = 'Web',
    premiumAmount: string = '',
    coverageAmount: string = ''
): void => {
    const detail: IAnalyticsFieldError = {
        segmentTitle,
        channel,
        productCode,
        premiumAmount,
        coverageAmount,
        fieldName,
        errorValue
    };
    document.dispatchEvent(new CustomEvent(evtName, { detail }));
};

export const fireBannerEventBasic = (
    evtName: AnalyticsEvent,
    segmentTitle: string,
    bannerMessage: string,
    productCode: string = '',
    channel: string = 'Web',
    premiumAmount: string = '',
    coverageAmount: string = '',
    fieldAction: string = 'Displayed'
): void => {
    const detail: IAnalyticsBanner = {
        segmentTitle,
        channel,
        productCode,
        premiumAmount,
        coverageAmount,
        bannerMessage,
        fieldAction
    };
    document.dispatchEvent(new CustomEvent(evtName, { detail }));
};
